import { memo, useEffect, useState } from 'react';
import { Skeleton } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useDebounce } from '@aimsos/hooks';
import { useIntegrations } from '../context';
import { INTEGRATIONS_TABS } from '../constants';
import { Search } from 'lucide-react';

/**
 * Integrations home hero (CT-4157 / CT-4993) — pixel-perfect to prototype `.mkt-hero`.
 * Copy and chrome switch with the Catalog tab (Already integrated vs Marketplace).
 */
const HERO_SECTION_CLASS = [
  'relative overflow-hidden text-center',
  'border-b border-edge dark:border-white/10',
  'px-[32px] pt-[56px]',
  'bg-[radial-gradient(ellipse_at_50%_0%,rgba(43,127,255,0.06)_0%,transparent_60%),linear-gradient(180deg,rgba(6,182,212,0.03)_0%,transparent_100%)]',
  'dark:bg-[radial-gradient(ellipse_at_50%_0%,rgba(43,127,255,0.10)_0%,transparent_60%),linear-gradient(180deg,rgba(6,182,212,0.04)_0%,transparent_100%)]',
].join(' ');

const HERO_GLOW_CLASS = [
  'pointer-events-none absolute left-1/2 top-[-100px] h-[200px] w-[600px] -translate-x-1/2 blur-[40px]',
  'bg-[radial-gradient(circle,rgba(43,127,255,0.10),transparent_70%)]',
  'dark:bg-[radial-gradient(circle,rgba(43,127,255,0.18),transparent_70%)]',
].join(' ');

const HERO_INPUT_CLASS = [
  'w-full rounded-[8px] border border-edge bg-surface',
  'py-[11px] pl-[44px] pr-[18px] text-[14px] text-content',
  'shadow-[0_4px_20px_rgba(15,23,42,0.06)] outline-none',
  'transition-[border-color,background-color,box-shadow] duration-150',
  'placeholder:text-content-faint',
  'focus:border-[#2B7FFF] focus:shadow-[0_0_0_4px_rgba(43,127,255,0.10),0_4px_20px_rgba(15,23,42,0.10)]',
  'dark:border-white/10 dark:bg-[#0D1120] dark:shadow-[0_4px_20px_rgba(0,0,0,0.18)]',
  'dark:placeholder:text-white/[0.38]',
  'dark:focus:border-[#2B7FFF] dark:focus:bg-[#0f172e]',
  'dark:focus:shadow-[0_0_0_4px_rgba(43,127,255,0.16),0_4px_20px_rgba(0,0,0,0.25)]',
  '[&::-webkit-search-cancel-button]:hidden',
].join(' ');

function IntegrationsHero() {
  const { t } = useTranslation('admin-studio');
  const {
    stats,
    statsLoading,
    marketplaceStats,
    marketplaceStatsLoading,
    searchQuery,
    setSearchQuery,
    openRequestIntegration,
    activeTab,
  } = useIntegrations();

  const [draft, setDraft] = useState(searchQuery);
  const debouncedQuery = useDebounce(draft, 300);

  const isMarketplace = activeTab === INTEGRATIONS_TABS.MARKETPLACE;

  const activeCount = stats.active_count ?? 0;
  const instances = stats.instances_count ?? 0;
  const categories = stats.categories_count ?? 0;
  const needsAttention = stats.needs_attention_count ?? 0;

  const title = isMarketplace
    ? t('integrations.hero.marketplace.title')
    : t(activeCount === 1 ? 'integrations.hero.titleOne' : 'integrations.hero.title', {
        count: activeCount,
      });

  const statsLine = isMarketplace
    ? t('integrations.hero.marketplace.subtitle', {
        count: marketplaceStats?.catalog_count ?? 0,
        categories: marketplaceStats?.categories_count ?? 0,
      })
    : t('integrations.hero.stats', {
        instances,
        categories,
        instanceLabel: t(
          instances === 1 ? 'integrations.hero.instanceOne' : 'integrations.hero.instanceOther',
        ),
        categoryLabel: t(
          categories === 1 ? 'integrations.hero.categoryOne' : 'integrations.hero.categoryOther',
        ),
      });

  const searchPlaceholder = isMarketplace
    ? t('integrations.hero.marketplace.searchPlaceholder')
    : t('integrations.hero.searchPlaceholder');

  const headingLoading = isMarketplace ? marketplaceStatsLoading : statsLoading;

  const marketplaceTiles = [
    {
      key: 'active',
      value: stats.active_count ?? 0,
      labelKey: 'integrations.hero.marketplace.statActive',
    },
    {
      key: 'official',
      value: marketplaceStats?.official_count ?? 0,
      labelKey: 'integrations.hero.marketplace.statOfficial',
    },
    {
      key: 'partners',
      value: marketplaceStats?.partner_count ?? 0,
      labelKey: 'integrations.hero.marketplace.statPartners',
    },
    {
      key: 'private',
      value: marketplaceStats?.private_count ?? 0,
      labelKey: 'integrations.hero.marketplace.statPrivate',
    },
  ];

  useEffect(() => {
    setSearchQuery(debouncedQuery);
  }, [debouncedQuery, setSearchQuery]);

  return (
    <section className={`${HERO_SECTION_CLASS} ${isMarketplace ? 'pb-[44px]' : 'pb-[28px]'}`}>
      <div aria-hidden="true" className={HERO_GLOW_CLASS} />

      {headingLoading ? (
        <div className="relative mx-auto flex max-w-[540px] flex-col items-center gap-[10px] pt-[12px]">
          <Skeleton className="h-8 w-[26rem] max-w-full" />
          <Skeleton className="mb-[26px] h-4 w-80 max-w-full" />
        </div>
      ) : (
        <>
          <h1 className="relative mb-[10px] pt-[12px] text-[32px] font-bold leading-[1.2] tracking-[-0.02em] text-content">
            {title}
          </h1>
          <p className="relative mx-auto mb-[26px] max-w-[540px] text-[14px] leading-[1.6] text-content-secondary dark:text-white/60">
            {isMarketplace ? (
              statsLine
            ) : (
              <>
                {statsLine}{' '}
                {needsAttention > 0 && (
                  <>
                    <strong className="font-bold text-[#FDC700]">
                      {t('integrations.hero.needAttention', { count: needsAttention })}
                    </strong>{' '}
                  </>
                )}
                {t('integrations.hero.tagline')}
              </>
            )}
          </p>
        </>
      )}

      <div className="relative mx-auto mb-[22px] w-full max-w-[560px]">
        <Search
          size={18}
          aria-hidden="true"
          className="pointer-events-none absolute left-[18px] top-1/2 -translate-y-1/2 text-content-faint dark:text-white/[0.38]"
        />
        <input
          type="search"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          className={HERO_INPUT_CLASS}
        />
      </div>

      {isMarketplace && (
        marketplaceStatsLoading ? (
          <div className="relative mx-auto mb-[22px] grid max-w-[640px] grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4">
            {marketplaceTiles.map((tile) => (
              <Skeleton key={tile.key} className="mx-auto h-10 w-24" />
            ))}
          </div>
        ) : (
          <div className="relative mx-auto mb-[22px] grid max-w-[640px] grid-cols-2 gap-x-6 gap-y-2 text-center sm:grid-cols-4">
            {marketplaceTiles.map((tile) => (
              <div key={tile.key} className="px-1 text-[12px] text-content-secondary">
                <strong className="mb-px block text-[14px] font-bold text-content">{tile.value}</strong>
                {t(tile.labelKey)}
              </div>
            ))}
          </div>
        )
      )}

      <div className="relative flex justify-center">
        <div className="inline-flex items-center gap-2 rounded-[24px] border border-[#FDC700]/20 bg-[#FDC700]/[0.06] px-[14px] py-2 text-[12px] text-content-secondary dark:text-white/60">
          {t('integrations.hero.requestPrompt')}
          <button
            type="button"
            onClick={() => openRequestIntegration()}
            className="font-bold text-[#FDC700] hover:underline focus-visible:outline-none focus-visible:underline"
          >
            {t('integrations.hero.requestAnchor')} →
          </button>
        </div>
      </div>
    </section>
  );
}

export default memo(IntegrationsHero);
